import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Radio, Plug, PlugZap, QrCode, Bot, AlertTriangle, Flame, Trash2, GraduationCap, ShieldAlert, BellRing } from 'lucide-react';
import type { Channel } from '@conversia/shared-types';
import * as channelsApi from '@/api/channels.api';
import * as agentsApi from '@/api/agents.api';
import * as trainingApi from '@/api/training.api';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/common/PageHeader';
import { ChannelConnectModal } from '@/components/channel/ChannelConnectModal';
import { QrModal } from '@/components/channel/QrModal';
import { channelStatusBadge, channelTypeLabel, channelWarmupLabel, puedeSacarDeSimulacro, quienAtiende } from '@/lib/badges';
import { useChannelsStore } from '@/store/channels.store';
import { useToast } from '@/hooks/useToast';
import { useVeRuta } from '@/hooks/useMenu';

/**
 * El candado de simulacro de un canal, a la vista (7-oct): todo canal nace con él y, hasta
 * sacarlo, no le manda nada a nadie. Sacarlo pide escribir el nombre (lo exige el servidor);
 * volver a ponerlo es el freno y no pide nada. Sin `onSacar` no se ofrece sacarlo: un canal
 * sin conectar (o el «Historial importado», que no tiene teléfono) no tiene a quién mandarle.
 * Sin estado: lo prueba renderToStaticMarkup.
 */
export function AvisoSimulacro({
  enSimulacro,
  conectado,
  onSacar,
  onPoner,
}: {
  enSimulacro: boolean;
  conectado: boolean;
  onSacar?: () => void;
  onPoner: () => void;
}) {
  if (!enSimulacro) {
    // El freno queda a mano también sin conectar: al reconectarlo mandaría de inmediato.
    return (
      <p className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
        <span className="flex-1">{conectado ? 'Manda mensajes de verdad.' : 'Fuera de simulacro: al conectarlo manda de verdad.'}</span>
        <button type="button" onClick={onPoner} className="shrink-0 font-medium text-slate-600 hover:underline dark:text-slate-300">
          Poner en simulacro
        </button>
      </p>
    );
  }
  return (
    <div className="flex items-start gap-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
      <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span className="flex-1">En simulacro: recibe mensajes, pero no le manda nada a nadie.</span>
      {onSacar && (
        <button type="button" onClick={onSacar} className="shrink-0 font-semibold underline">
          Sacar de simulacro
        </button>
      )}
    </div>
  );
}

/**
 * El canal de los avisos al dueño (16-oct; antes era un SQL). Solo lo ve la administración.
 * `elegido`: este canal es el de avisos; `puedeMandar`: el elegido está vivo. Sin estado.
 */
export function CanalDeAvisos({
  elegido,
  puedeMandar,
  onElegir,
}: {
  elegido: boolean;
  puedeMandar: boolean;
  onElegir: () => void;
}) {
  if (elegido) {
    return (
      <p className="flex items-start gap-1.5 rounded-md bg-brand-50 px-2.5 py-1.5 text-xs text-brand-700 dark:bg-brand-900/20 dark:text-brand-300">
        <BellRing className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Por aquí le llegan los avisos al dueño.
          {!puedeMandar && ' Ahora no puede mandar: los avisos esperan en el buzón hasta que se conecte.'}
        </span>
      </p>
    );
  }
  return (
    <button
      type="button"
      onClick={onElegir}
      className="flex items-center gap-1.5 self-start text-xs font-medium text-slate-600 hover:underline dark:text-slate-300"
    >
      <BellRing className="h-3.5 w-3.5" /> Usar para los avisos al dueño
    </button>
  );
}

export default function Channels() {
  const qc = useQueryClient();
  const toast = useToast();
  const setStatus = useChannelsStore((s) => s.setStatus);
  const [connectOpen, setConnectOpen] = useState(false);
  const [qrChannel, setQrChannel] = useState<Channel | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Channel | null>(null);
  const [simulacroTarget, setSimulacroTarget] = useState<Channel | null>(null);
  const [nombreEscrito, setNombreEscrito] = useState('');
  const cerrarSimulacro = () => {
    setSimulacroTarget(null);
    setNombreEscrito('');
  };
  // 8-oct: qué agente contesta en un canal, y callarlo para entrenar, es configuración del
  // agente: lo ve y lo cambia la administración del sistema. El dueño del negocio conecta,
  // desconecta y frena sus canales, y aquí solo lee quién atiende.
  const configura = useVeRuta('/agents');

  const { data: channels, isLoading } = useQuery({
    queryKey: ['channels', 'list'],
    queryFn: () => channelsApi.listChannels(),
  });

  // 16-oct: el canal de avisos al dueño, solo para la administración (la API da 403 al resto).
  const { data: avisos } = useQuery({
    queryKey: ['channels', 'avisos-generales'],
    queryFn: () => channelsApi.getAvisosGenerales(),
    enabled: configura,
  });
  const elegirAvisos = useMutation({
    mutationFn: (c: Channel) => channelsApi.setAvisosGenerales(c.id),
    onSuccess: (_r, c) => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      toast.success(`Los avisos al dueño salen ahora por «${c.name}»`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo elegir el canal de avisos'),
  });

  // Agentes ACTIVE para el selector de "agente por defecto" del canal (Bug #3).
  const { data: agents } = useQuery({
    queryKey: ['agents', 'list', 'ACTIVE'],
    queryFn: () => agentsApi.listAgents({ status: 'ACTIVE', take: 200 }),
  });

  const setDefaultAgent = useMutation({
    mutationFn: ({ id, defaultAgentId }: { id: string; defaultAgentId: string | null }) =>
      channelsApi.updateChannel(id, { defaultAgentId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      toast.info('Agente por defecto actualizado');
    },
    onError: () => toast.error('No se pudo actualizar el agente'),
  });

  const connect = useMutation({
    mutationFn: (channel: Channel) => channelsApi.connectChannel(channel.id),
    onSuccess: (result, channel) => {
      setStatus(channel.id, result.status, result.qr);
      void qc.invalidateQueries({ queryKey: ['channels'] });
      setQrChannel(channel);
    },
    onError: () => toast.error('No se pudo iniciar la conexión'),
  });

  const disconnect = useMutation({
    mutationFn: (id: string) => channelsApi.disconnectChannel(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      toast.info('Canal desconectado');
    },
    onError: () => toast.error('No se pudo desconectar'),
  });

  // Modo entrenamiento a nivel canal: el agente calla en TODO el canal y las
  // respuestas humanas se capturan como ejemplos (tanda sala-maquinas).
  const channelTraining = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) =>
      trainingApi.setChannelTraining(id, on),
    onSuccess: (_r, { on }) => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      toast.success(
        on
          ? 'Canal en modo entrenamiento: el agente no responde en este canal'
          : 'Entrenamiento del canal terminado: el agente vuelve a responder',
      );
    },
    onError: () => toast.error('No se pudo cambiar el modo entrenamiento del canal'),
  });

  const sacarDeSimulacro = useMutation({
    mutationFn: (c: Channel) => channelsApi.quitarSimulacro(c.id, nombreEscrito),
    onSuccess: (_r, c) => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      toast.success(`«${c.name}» ya manda mensajes de verdad`);
      cerrarSimulacro();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo sacar del simulacro'),
  });

  const ponerEnSimulacro = useMutation({
    mutationFn: (c: Channel) => channelsApi.ponerSimulacro(c.id),
    onSuccess: (_r, c) => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      toast.info(`«${c.name}» quedó en simulacro: ya no manda nada`);
    },
    onError: () => toast.error('No se pudo poner en simulacro'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => channelsApi.deleteChannel(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      toast.success('Canal eliminado');
      setDeleteTarget(null);
    },
    onError: () => toast.error('No se pudo eliminar el canal'),
  });

  const items = channels ?? [];

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Canales"
        helpKey="channels"
        description="Conecta WhatsApp y otras plataformas para recibir conversaciones."
        action={
          <Button onClick={() => setConnectOpen(true)}>
            <Plus className="h-4 w-4" /> Conectar canal
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size={28} />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Radio}
          title="No hay canales conectados"
          description="Conecta tu primer canal de WhatsApp para empezar a recibir mensajes."
          action={
            <Button onClick={() => setConnectOpen(true)}>
              <Plus className="h-4 w-4" /> Conectar canal
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((channel) => {
            const badge = channelStatusBadge(channel.status);
            const isConnected = channel.status === 'CONNECTED';
            const warmup = channelWarmupLabel(channel);
            return (
              <Card key={channel.id}>
                <CardBody className="flex flex-col gap-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                        <Radio className="h-5 w-5" />
                      </span>
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">{channel.name}</h3>
                        <p className="text-xs text-slate-400">{channelTypeLabel[channel.type]}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                      {warmup && !warmup.done && (
                        <Badge variant="warning" title={warmup.tooltip}>
                          <Flame className="h-3 w-3" /> {warmup.badge}
                        </Badge>
                      )}
                      {channel.trainingMode && <Badge variant="warning">Entrenando</Badge>}
                      {channel.simulacro && <Badge variant="warning">Simulacro</Badge>}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 border-t border-surface-border pt-3 dark:border-slate-800">
                    {isConnected ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => disconnect.mutate(channel.id)}
                        loading={disconnect.isPending && disconnect.variables === channel.id}
                      >
                        <Plug className="h-4 w-4" /> Desconectar
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => connect.mutate(channel)}
                        loading={connect.isPending && connect.variables?.id === channel.id}
                      >
                        <PlugZap className="h-4 w-4" /> Conectar
                      </Button>
                    )}
                    {!isConnected && (
                      <Button variant="ghost" size="sm" onClick={() => setQrChannel(channel)}>
                        <QrCode className="h-4 w-4" /> Ver QR
                      </Button>
                    )}
                    {configura && (
                      <Button
                        variant={channel.trainingMode ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() =>
                          channelTraining.mutate({ id: channel.id, on: !channel.trainingMode })
                        }
                        loading={
                          channelTraining.isPending && channelTraining.variables?.id === channel.id
                        }
                        title={
                          channel.trainingMode
                            ? 'Terminar el entrenamiento del canal'
                            : 'Modo entrenamiento: el agente calla en todo el canal'
                        }
                      >
                        <GraduationCap className="h-4 w-4" />
                        {channel.trainingMode ? 'Terminar' : 'Entrenar'}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto text-danger hover:bg-red-50 hover:text-danger dark:hover:bg-red-900/20"
                      onClick={() => setDeleteTarget(channel)}
                    >
                      <Trash2 className="h-4 w-4" /> Eliminar
                    </Button>
                  </div>

                  {channel.lastError && !isConnected && (
                    <div className="flex items-start gap-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>{channel.lastError}</span>
                    </div>
                  )}

                  <AvisoSimulacro
                    enSimulacro={Boolean(channel.simulacro)}
                    conectado={isConnected}
                    onSacar={puedeSacarDeSimulacro(channel) ? () => setSimulacroTarget(channel) : undefined}
                    onPoner={() => ponerEnSimulacro.mutate(channel)}
                  />

                  {configura && channel.type === 'WHATSAPP' && avisos && (
                    <CanalDeAvisos
                      elegido={avisos.configuradoId === channel.id}
                      puedeMandar={avisos.efectivoId === channel.id}
                      onElegir={() => elegirAvisos.mutate(channel)}
                    />
                  )}

                  {!configura && (
                    <p className="flex items-center gap-1.5 border-t border-surface-border pt-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                      <Bot className="h-3.5 w-3.5" />
                      {quienAtiende(channel.defaultAgentId, agents?.items)}
                    </p>
                  )}
                  {configura && (
                    <label className="flex flex-col gap-1 border-t border-surface-border pt-3 text-xs dark:border-slate-800">
                      <span className="flex items-center gap-1.5 font-medium text-slate-500 dark:text-slate-400">
                        <Bot className="h-3.5 w-3.5" /> Agente por defecto
                      </span>
                      <select
                        value={channel.defaultAgentId ?? ''}
                        disabled={setDefaultAgent.isPending}
                        onChange={(e) =>
                          setDefaultAgent.mutate({
                            id: channel.id,
                            defaultAgentId: e.target.value || null,
                          })
                        }
                        className="h-9 w-full rounded-md border border-surface-border bg-white px-2 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                      >
                        <option value="">Sin agente (responde un humano)</option>
                        {agents?.items.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      <ChannelConnectModal
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        onCreated={(channel) => connect.mutate(channel)}
      />
      <QrModal channel={qrChannel} open={Boolean(qrChannel)} onClose={() => setQrChannel(null)} />

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => !remove.isPending && setDeleteTarget(null)}
        title="Eliminar canal"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={remove.isPending}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => deleteTarget && remove.mutate(deleteTarget.id)}
            >
              <Trash2 className="h-4 w-4" /> Eliminar
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          ¿Eliminar canal <span className="font-semibold">{deleteTarget?.name}</span>? Esta acción no se
          puede deshacer. Las conversaciones y mensajes asociados se mantendrán como histórico.
        </p>
      </Modal>

      <Modal
        open={Boolean(simulacroTarget)}
        onClose={() => !sacarDeSimulacro.isPending && cerrarSimulacro()}
        title="Sacar de simulacro"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={cerrarSimulacro} disabled={sacarDeSimulacro.isPending}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={sacarDeSimulacro.isPending}
              disabled={nombreEscrito.trim() !== simulacroTarget?.name.trim()}
              onClick={() => simulacroTarget && sacarDeSimulacro.mutate(simulacroTarget)}
            >
              Sí, que mande de verdad
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
          <p>
            Desde ese momento <span className="font-semibold">{simulacroTarget?.name}</span> manda mensajes de
            verdad a teléfonos de verdad: el agente le contesta a quien escriba.
          </p>
          <label className="flex flex-col gap-1">
            <span>
              Para confirmar, escribe el nombre del canal: <span className="font-semibold">{simulacroTarget?.name}</span>
            </span>
            <input
              value={nombreEscrito}
              onChange={(e) => setNombreEscrito(e.target.value)}
              autoComplete="off"
              className="h-9 w-full rounded-md border border-surface-border bg-white px-2 text-sm focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            />
          </label>
        </div>
      </Modal>
    </div>
  );
}
